import Logger, { isNode } from './IsomorphicLogger';
import { defer } from './defer';
import exponentialDelayFactory from './exponentialDelay';

// Timers for use with later
const laterTimers = {};

// For awaiting all promises in scripts
let laterWaitList = [];

// Exporting this internal trap so we can use the trapping logic apart from the later delay,
// for example in cron jobs
export const trapAsyncErrors = async (
	callbackOrPromise,
	{
		laterStack = new Error('stack trace please').stack,
		logger = Logger,
		disableLoggerAlert,
	} = {},
) => {
	let result;
	try {
		const promise =
			typeof callbackOrPromise === 'function'
				? callbackOrPromise()
				: callbackOrPromise;

		if (promise && typeof promise.catch === 'function') {
			result = await promise.catch((ex) => {
				result = ex;

				const context = {
					message: ex?.message,
					errorStack: ex?.stack,
					laterStack,
					originalException: ex,
				};

				logger.error(`Error in promise given to later:`, context);
				if (!isNode) {
					logger.error(ex);
				}

				// .alert is only relevant on the server
				if (isNode) {
					Logger.alert(`AsyncErrorTrap-PromiseRejected`, context);
				}
			});
		}
	} catch (ex) {
		result = ex;

		const context = {
			message: ex?.message,
			errorStack: ex?.stack,
			laterStack,
			originalException: ex,
		};

		// console.error(`\n\n>>>odd logger`, logger, new Error('odd from').stack);
		logger.error(`Error caught in trapAsyncErrors():`, context);

		// .alert is only relevant on the server
		if (isNode && !disableLoggerAlert) {
			Logger.alert(`AsyncErrorTrap-CaughtException`, context);
		}
	}

	return result;
};

// Arbitrary delay to move 'later' further out from UX-relevant interactions
// const DelayFactory = () => {
// 	return 100 + Math.ceil(Math.random() * 200);
// };

const DelayFactory = exponentialDelayFactory({
	initialDelay: 100,
	maxDelay: 3333,
	multiplier: 1.125,
});

/**
 * Simple utility to move a given promise out of the event loop.
 * Useful for when you don't care about the return value, but still want to catch errors. This will
 * .catch() errors and log using the given (or default) Logger.
 *
 * @param {function|Promise} callbackOrPromise Function to call or promise to await. Function is assumed async, and as such, assumed to return a promise which will be awaited.
 * @param {Object} options (optional)
 * @param {Logger} options.logger (default: `Logger`) Logger instance to use for error logging
 * @param {number} options.delay (default: some non-zero number) Delay to use before awaiting the promise/calling the callback. Delay of 0 just moves it to the next event loop.
 * @param {boolean} options.immediate (default: `false`) If true, does NOT delay till next tick, just wraps the `callbackOrPromise` with error catching and returns an `await`-able value
 */
export const later = (callbackOrPromise, options) => {
	const { stack: laterStack } = new Error('later called from...');

	const finished = defer();

	const factoryDelay = DelayFactory();

	let {
		logger = Logger,
		delay = factoryDelay,
		key,
		immediate = false,
		debugDelay = false,
	} = typeof options === 'number' ? { delay: options } : options || {};

	if (laterTimers[key]) {
		clearTimeout(laterTimers[key]);
	}

	if (immediate) {
		return trapAsyncErrors(callbackOrPromise, { laterStack, logger });
	}

	if (debugDelay) {
		logger.debug(` > Using delay: `, delay);
	}

	const timerId = setTimeout(async () => {
		await trapAsyncErrors(callbackOrPromise, { laterStack, logger });
		finished.resolve();

		// Lower the accumulated delay pressure, otherwise we'll always stay at the highest
		// delay after a few later() calls
		DelayFactory.reduce();

		laterWaitList = laterWaitList.filter((x) => x !== finished);
	}, delay);

	if (key) {
		laterTimers[key] = timerId;
	}

	laterWaitList.push(finished);

	return Promise.resolve(finished);
};

export const waitForAllLater = () => Promise.all(laterWaitList);

/**
 * Returns a function that wraps later() and injects the logger
 * @param {Logger} logger
 * @returns {Function} later() with the given logger injected into the props
 */
export const getLaterWithLogger = (logger) => (callback, options) =>
	later(callback, { ...options, logger });
